import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@shared/lib';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  kicker?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
  className?: string;
  closeOnBackdrop?: boolean;
  /** sheet — на телефоне шторка снизу во всю ширину, с `sm` — окно по центру; длинное содержимое прокручивается
   *  под неподвижной шапкой. По умолчанию — окно по центру. */
  placement?: 'center' | 'sheet';
  /** Узкие поля для окон с длинным текстом (разбор ответа в игре, владелец 2026-10-27: «много отступа во внешнем
   *  контейнере»): 12 px на телефоне вместо 20, 20 px с `sm` вместо 28. */
  tight?: boolean;
}

export function Modal({
  open,
  onClose,
  kicker,
  title,
  children,
  footer,
  width = 520,
  className,
  closeOnBackdrop = true,
  placement = 'center',
  tight = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Запоминаем активный элемент, чтобы вернуть фокус после закрытия.
    lastFocusedRef.current = document.activeElement as HTMLElement | null;

    const getFocusable = (): HTMLElement[] => {
      const dialog = dialogRef.current;
      if (!dialog) return [];
      return Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),textarea,input:not([disabled]),select,[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
    };

    // Переводим фокус внутрь модалки (первый интерактивный элемент или сам диалог).
    (getFocusable()[0] ?? dialogRef.current)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      // Focus-trap: Tab/Shift+Tab не выпускают фокус за пределы диалога.
      const items = getFocusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      lastFocusedRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  const sheet = placement === 'sheet';

  return createPortal(
    <>
      <div
        onClick={closeOnBackdrop ? onClose : undefined}
        className="fixed inset-0 z-[1200] animate-fadeIn bg-[rgba(8,8,10,0.62)]"
      />
      <div
        className={cn(
          'pointer-events-none fixed inset-0 z-[1201] flex justify-center',
          sheet ? 'items-end sm:items-center sm:p-4' : 'items-center p-4',
        )}
      >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Диалог'}
        tabIndex={-1}
        className={cn(
          'pointer-events-auto overflow-hidden border border-line bg-card outline-none',
          'shadow-[0_32px_80px_-24px_rgba(0,0,0,0.5)]',
          sheet
            ? 'flex max-h-[88dvh] w-full flex-col rounded-t-card border-b-0 animate-cm-dialog-in sm:w-[94vw] sm:rounded-card sm:border-b'
            : 'w-[94vw] rounded-card animate-pop',
          className,
        )}
        style={{ maxWidth: width }}
      >
        {(kicker || title) && (
          <div
            className={cn(
              'flex items-center justify-between',
              tight ? 'px-3 pt-4 sm:px-5 sm:pt-5' : sheet ? 'px-5 pt-5 sm:px-7 sm:pt-6' : 'px-7 pt-6',
              sheet && 'shrink-0',
            )}
          >
            <div>
              {kicker && (
                <div className="mb-1.5 text-[12px] font-medium uppercase tracking-[0.16em] text-muted">{kicker}</div>
              )}
              {title && (
                <div className="font-serif text-[28px] font-medium leading-none tracking-[-0.02em]">{title}</div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={sheet ? 'Закрыть' : undefined}
              className="rounded-pill bg-elev px-3 py-1.5 text-[12px] font-medium uppercase tracking-[0.14em] text-muted transition-colors hover:text-fg"
            >
              {/* Шторка открывается и на телефоне, где клавиши Esc нет. */}
              {sheet ? <span aria-hidden className="text-[14px] leading-none">✕</span> : 'esc'}
            </button>
          </div>
        )}
        {children && (
          <div
            className={cn(
              'flex flex-col gap-4',
              sheet && 'min-h-0 overflow-y-auto overscroll-contain',
              tight
                ? 'px-3 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 sm:px-5 sm:pb-5'
                : sheet
                  ? 'px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-5 sm:px-7 sm:pb-7'
                  : 'px-7 pb-7 pt-6',
            )}
          >
            {children}
          </div>
        )}
        {footer && (
          <div className="flex items-center justify-between border-t border-line bg-elev px-7 py-4">{footer}</div>
        )}
      </div>
      </div>
    </>,
    document.body,
  );
}
